import { Controller } from "@hotwired/stimulus"

import {
  canonicalValue,
  isTimeDisabled,
  parseCanonical,
} from "./time_picker_helpers"

// Connects to data-controller="ui--time-picker-scrollable"
//
// Inline scrollable time selector matching @a-safe-digital/react-time-picker's
// TimePickerScrollable (the building block of the range picker). Hour/minute[/second]
// columns of toggle buttons (Ui::ButtonComponent, styled via data-active) with
// keyboard navigation. Buttons are server-rendered over the full range; this
// controller toggles active/disabled state — out-of-range values are disabled
// (not filtered) so a changing bound (range Start→End) needs no DOM rebuild.
// A hidden input carries the canonical 24h value. CONEK-1580.
export default class extends Controller {
  static targets = ["hiddenInput", "hourList", "minuteList", "secondList"]

  static values = {
    hourFormat: Number,
    showSeconds: Boolean,
    minTime: Object,
    maxTime: Object,
    value: String,
  }

  connect() {
    this.hourFormat = this.hourFormatValue === 12 ? 12 : 24
    this.showSeconds = this.showSecondsValue
    this.minTime = this.normalizeBound(this.minTimeValue)
    this.maxTime = this.normalizeBound(this.maxTimeValue)
    this.parts = parseCanonical(this.valueValue) || {
      hour: 0,
      minute: 0,
      second: 0,
    }
    this._connected = true
    this.refresh()
    setTimeout(() => this.scrollToSelected(), 100)
  }

  disconnect() {
    this._connected = false
  }

  normalizeBound(obj) {
    return obj && obj.hour != null ? obj : null
  }

  // Re-filter/disable when a bound changes (range Start updates End's minTime).
  minTimeValueChanged() {
    if (!this._connected) return
    this.minTime = this.normalizeBound(this.minTimeValue)
    this.refresh()
  }

  maxTimeValueChanged() {
    if (!this._connected) return
    this.maxTime = this.normalizeBound(this.maxTimeValue)
    this.refresh()
  }

  selectHour(event) {
    if (event.currentTarget.disabled) return
    this.parts.hour = Number(event.currentTarget.dataset.hour)
    this.refresh()
  }

  selectMinute(event) {
    if (event.currentTarget.disabled) return
    this.parts.minute = Number(event.currentTarget.dataset.minute)
    this.refresh()
  }

  selectSecond(event) {
    if (event.currentTarget.disabled) return
    this.parts.second = Number(event.currentTarget.dataset.second)
    this.refresh()
  }

  onKeydown(event) {
    const kind = event.currentTarget.dataset.kind
    const list = this.listFor(kind)
    if (!list) return
    const buttons = this.enabledButtons(list, kind)
    if (!buttons.length) return

    const currentValue = this.parts[kind]
    let index = buttons.findIndex(
      (b) => Number(b.dataset[kind]) === currentValue,
    )
    if (index === -1) index = 0

    switch (event.key) {
      case "ArrowUp":
        event.preventDefault()
        index = index > 0 ? index - 1 : buttons.length - 1
        break
      case "ArrowDown":
        event.preventDefault()
        index = index < buttons.length - 1 ? index + 1 : 0
        break
      case "Home":
        event.preventDefault()
        index = 0
        break
      case "End":
        event.preventDefault()
        index = buttons.length - 1
        break
      default:
        return
    }

    const target = buttons[index]
    this.parts[kind] = Number(target.dataset[kind])
    this.refresh()
    target.focus()
    target.scrollIntoView({ block: "center", behavior: "smooth" })
  }

  refresh() {
    const { hour, minute, second } = this.parts

    this.eachButton(this.hourListTarget, (btn) => {
      const h = Number(btn.dataset.hour)
      this.setState(
        btn,
        h === hour,
        isTimeDisabled(h, minute, 0, this.minTime, this.maxTime),
      )
    })

    this.eachButton(this.minuteListTarget, (btn) => {
      const m = Number(btn.dataset.minute)
      this.setState(
        btn,
        m === minute,
        isTimeDisabled(hour, m, 0, this.minTime, this.maxTime),
      )
    })

    if (this.showSeconds && this.hasSecondListTarget) {
      this.eachButton(this.secondListTarget, (btn) => {
        const s = Number(btn.dataset.second)
        this.setState(
          btn,
          s === second,
          isTimeDisabled(hour, minute, s, this.minTime, this.maxTime),
        )
      })
    }

    this.hiddenInputTarget.value = canonicalValue(
      hour,
      minute,
      second,
      this.showSeconds,
    )
    this.dispatch("change", { detail: { value: this.hiddenInputTarget.value } })
  }

  listFor(kind) {
    if (kind === "hour") return this.hourListTarget
    if (kind === "minute") return this.minuteListTarget
    if (kind === "second" && this.hasSecondListTarget)
      return this.secondListTarget
    return null
  }

  enabledButtons(list, kind) {
    return Array.from(
      list.querySelectorAll(`button[data-kind='${kind}']`),
    ).filter((b) => !b.disabled)
  }

  eachButton(list, fn) {
    list.querySelectorAll("button[role='option']").forEach(fn)
  }

  setState(btn, selected, disabled) {
    btn.dataset.selected = String(selected)
    btn.dataset.active = String(selected)
    btn.setAttribute("aria-pressed", String(selected))
    btn.disabled = disabled
  }

  scrollToSelected() {
    const lists = [this.hourListTarget, this.minuteListTarget]
    if (this.showSeconds && this.hasSecondListTarget)
      lists.push(this.secondListTarget)
    lists.forEach((list) => {
      const selected = list.querySelector('[data-selected="true"]')
      if (selected)
        selected.scrollIntoView({ block: "center", behavior: "smooth" })
    })
  }
}
